/**
 * Utilities for essay content type.
 */

import type { PageElementData } from '@ai-content-hub/content-elements';
import type { BlockContent } from '@ai-content-hub/content-elements';

function extractTextFromTiptapJson(node: unknown): string {
    if (!node) return '';

    if (typeof node === 'string') return node;

    if (Array.isArray(node)) {
        return node.map(extractTextFromTiptapJson).join(' ').trim();
    }

    if (typeof node === 'object' && node !== null) {
        const obj = node as Record<string, unknown>;
        if (obj.type === 'text' && typeof obj.text === 'string') {
            return obj.text;
        }
        const content = obj.content;
        if (content) {
            return extractTextFromTiptapJson(content);
        }
    }

    return '';
}

function extractTextFromBlock(content: BlockContent | undefined): string {
    if (!content) return '';

    switch (content.type) {
        case 'text':
            return (content.text || '').trim();
        case 'rich-text': {
            const inner = content.content;
            if (typeof inner === 'string') return inner.trim();
            if (inner && typeof inner === 'object' && 'markdown' in inner) {
                const md = (inner as { markdown: string }).markdown;
                return typeof md === 'string' ? md.trim() : '';
            }
            return extractTextFromTiptapJson(inner).trim();
        }
        case 'audio':
            return (content.title || '').trim();
        case 'video':
            return (content.title || '').trim();
        case 'image':
            return (content.alt || '').trim();
        case 'office-document':
            return [content.title, content.fileName]
                .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
                .join(' — ');
        default:
            return '';
    }
}

/**
 * Extract and concatenate all text from essay lesson content (content tab 1).
 * Used as question_content when sending to the grading API.
 */
export function extractTextFromLessonContent(lessonContent: PageElementData | null | undefined): string {
    if (!lessonContent || typeof lessonContent !== 'object') return '';

    const sections = lessonContent.sections;
    if (!Array.isArray(sections)) return '';

    const parts: string[] = [];

    for (const section of sections) {
        if (!section || typeof section !== 'object') continue;
        const blocks = section.blocks;
        if (!Array.isArray(blocks)) continue;

        for (const block of blocks) {
            if (!block || typeof block !== 'object') continue;
            const text = extractTextFromBlock(block.content as BlockContent);
            if (text) parts.push(text);
        }
    }

    return parts.join('\n\n').trim();
}

/**
 * Compose the `question_content` sent to the AI grader (ELMS-2280).
 *
 * The essay task the student answers lives in `essayQuestion.question`; the
 * lesson content (tab 1) is only reading/reference material. Grading used to
 * send the lesson content alone and drop the task whenever the lesson had any
 * text, so the AI judged the answer against an unrelated passage and scored
 * "content" 0. The task now always comes first and is labelled; the lesson
 * text follows as supporting material when both exist.
 */
export function composeEssayQuestionContent(task: string, lessonText: string): string {
    const t = (task ?? '').trim();
    const l = (lessonText ?? '').trim();
    if (t && l) return `Essay task:\n${t}\n\nReference material:\n${l}`;
    return t || l;
}
